import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { toast } from 'sonner';

// ─── helpers ────────────────────────────────────────────────────────────────

const STATUS_COLORS = {
  connected: 'bg-green-100 text-green-700 border-green-200',
  connecting: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  qr: 'bg-blue-100 text-blue-700 border-blue-200',
  disconnected: 'bg-gray-100 text-gray-600 border-gray-200',
  error: 'bg-red-100 text-red-700 border-red-200',
};
const STATUS_LABELS = { connected: 'Conectado', connecting: 'Conectando…', qr: 'Aguardando QR', disconnected: 'Desconectado', error: 'Erro' };

function StatusBadge({ status }) {
  return (
    <span className={`inline-flex items-center text-xs font-semibold px-2.5 py-1 rounded-full border ${STATUS_COLORS[status] || STATUS_COLORS.disconnected}`}>
      <span className={`w-1.5 h-1.5 rounded-full mr-1.5 ${status === 'connected' ? 'bg-green-500' : status === 'qr' ? 'bg-blue-500' : status === 'connecting' ? 'bg-yellow-500' : 'bg-gray-400'}`} />
      {STATUS_LABELS[status] || status}
    </span>
  );
}

// ─── Synkly config section ────────────────────────────────────────────────────

function SynklyConfigBanner({ api, config, onSaved }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ base_url: '', api_key: '' });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!form.base_url || !form.api_key) { toast.error('Preencha todos os campos'); return; }
    setSaving(true);
    try {
      await api.post('/synkly/config', form);
      toast.success('Configuração Synkly salva!');
      setOpen(false);
      onSaved();
    } catch { toast.error('Erro ao salvar configuração'); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!window.confirm('Remover configuração Synkly?')) return;
    try {
      await api.delete('/synkly/config');
      toast.success('Configuração removida');
      onSaved();
    } catch { toast.error('Erro ao remover'); }
  };

  const copyWebhook = () => {
    if (config?.webhook_url) {
      navigator.clipboard.writeText(config.webhook_url);
      toast.success('URL do webhook copiada!');
    }
  };

  if (config?.configured) {
    return (
      <div className="space-y-2 mb-6">
        <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-xl px-4 py-3">
          <div className="flex items-center gap-3">
            <span className="text-green-500 text-xl">✅</span>
            <div>
              <p className="text-sm font-semibold text-green-800">Synkly conectado</p>
              <p className="text-xs text-green-600">{config.base_url} · chave: {config.api_key_prefix}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => setOpen(true)}>⚙️ Editar</Button>
            <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={handleDelete}>🗑️</Button>
          </div>
          <SynklyConfigDialog open={open} onClose={() => setOpen(false)} form={form} setForm={setForm} onSave={handleSave} saving={saving} existing={config} />
        </div>
        {config.webhook_url && (
          <div className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-xl px-4 py-2">
            <span className="text-blue-500 text-sm">🔗</span>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-blue-800 mb-0.5">URL do Webhook (configure no painel Synkly)</p>
              <p className="text-xs text-blue-600 font-mono truncate">{config.webhook_url}</p>
            </div>
            <Button size="sm" variant="outline" className="shrink-0 text-blue-700 border-blue-300 hover:bg-blue-100" onClick={copyWebhook}>📋 Copiar</Button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-2 mb-6">
      <div className="flex items-center justify-between bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
        <div className="flex items-center gap-3">
          <span className="text-amber-500 text-xl">⚠️</span>
          <div>
            <p className="text-sm font-semibold text-amber-800">Synkly não configurado</p>
            <p className="text-xs text-amber-600">Informe a URL da API e a API key para ativar WhatsApp e LinkedIn</p>
          </div>
        </div>
        <Button size="sm" onClick={() => setOpen(true)} className="bg-amber-600 hover:bg-amber-700 text-white">🔗 Configurar</Button>
        <SynklyConfigDialog open={open} onClose={() => setOpen(false)} form={form} setForm={setForm} onSave={handleSave} saving={saving} />
      </div>
      {config?.webhook_url && (
        <div className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-xl px-4 py-2">
          <span className="text-blue-500 text-sm">🔗</span>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold text-blue-800 mb-0.5">URL do Webhook para configurar no Synkly</p>
            <p className="text-xs text-blue-600 font-mono truncate">{config.webhook_url}</p>
          </div>
          <Button size="sm" variant="outline" className="shrink-0 text-blue-700 border-blue-300 hover:bg-blue-100" onClick={copyWebhook}>📋 Copiar</Button>
        </div>
      )}
    </div>
  );
}

function SynklyConfigDialog({ open, onClose, form, setForm, onSave, saving, existing }) {
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>⚙️ Configurar Synkly API</DialogTitle></DialogHeader>
        <div className="space-y-4 mt-2">
          <div>
            <Label>URL Base da API *</Label>
            <Input className="mt-1" placeholder="https://seu-synkly.com/api" value={form.base_url} onChange={e => setForm(f => ({ ...f, base_url: e.target.value }))} />
            <p className="text-xs text-gray-400 mt-1">Sem barra no final. Ex: https://api.synkly.com/api</p>
          </div>
          <div>
            <Label>API Key *</Label>
            <Input className="mt-1" type="password" placeholder="sk_live_..." value={form.api_key} onChange={e => setForm(f => ({ ...f, api_key: e.target.value }))} />
          </div>
          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button variant="outline" onClick={onClose}>Cancelar</Button>
            <Button onClick={onSave} disabled={saving}>{saving ? 'Salvando…' : 'Salvar'}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── WhatsApp Tab ─────────────────────────────────────────────────────────────

function WhatsAppTab({ api, configured }) {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const [creating, setCreating] = useState(false);
  const [qrAccount, setQrAccount] = useState(null);
  const [qrPolling, setQrPolling] = useState(false);
  const pollRef = useRef(null);

  const [checkNumbersAcc, setCheckNumbersAcc] = useState('');
  const [checkInput, setCheckInput] = useState('');
  const [checkResult, setCheckResult] = useState(null);
  const [checking, setChecking] = useState(false);

  const [bulkAcc, setBulkAcc] = useState('');
  const [bulkRecipients, setBulkRecipients] = useState('');
  const [bulkText, setBulkText] = useState('');
  const [bulkJob, setBulkJob] = useState(null);
  const [bulkSending, setBulkSending] = useState(false);
  const [jobStatus, setJobStatus] = useState(null);

  const [conversations, setConversations] = useState([]);
  const [convAcc, setConvAcc] = useState('');
  const [convLoading, setConvLoading] = useState(false);

  const [activeSection, setActiveSection] = useState('accounts');

  const whatsappAccounts = accounts.filter(a => a.channel === 'whatsapp');

  const fetchAccounts = async () => {
    if (!configured) return;
    setLoading(true);
    try {
      const res = await api.get('/synkly/accounts');
      setAccounts(res.data || []);
    } catch { toast.error('Erro ao carregar contas'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchAccounts(); }, [configured]);

  // QR polling
  useEffect(() => {
    if (!qrAccount) { clearInterval(pollRef.current); return; }
    setQrPolling(true);
    pollRef.current = setInterval(async () => {
      try {
        const res = await api.get(`/synkly/accounts/${qrAccount.id}`);
        const acc = res.data;
        if (acc.status === 'connected') {
          clearInterval(pollRef.current);
          setQrAccount(null);
          setQrPolling(false);
          toast.success('WhatsApp conectado! 🎉');
          fetchAccounts();
        } else if (acc.qr) {
          setQrAccount(acc);
        }
      } catch { clearInterval(pollRef.current); setQrPolling(false); }
    }, 4000);
    return () => clearInterval(pollRef.current);
  }, [qrAccount?.id]);

  const handleCreate = async () => {
    if (!newLabel) { toast.error('Informe um rótulo'); return; }
    setCreating(true);
    try {
      const res = await api.post('/synkly/accounts', { channel: 'whatsapp', label: newLabel });
      toast.success('Conta criada! Leia o QR code no WhatsApp.');
      setShowCreate(false); setNewLabel('');
      setQrAccount(res.data);
      fetchAccounts();
    } catch (e) { toast.error('Erro ao criar conta'); }
    finally { setCreating(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Desconectar e remover esta conta?')) return;
    try { await api.delete(`/synkly/accounts/${id}`); toast.success('Conta removida'); fetchAccounts(); }
    catch { toast.error('Erro ao remover conta'); }
  };

  const handleCheckQr = async (acc) => {
    try {
      const res = await api.get(`/synkly/accounts/${acc.id}`);
      setQrAccount(res.data);
    } catch { toast.error('Erro ao carregar QR'); }
  };

  const handleCheckNumbers = async () => {
    if (!checkNumbersAcc || !checkInput) { toast.error('Selecione uma conta e informe os números'); return; }
    const numbers = checkInput.split(/[\n,]/).map(n => n.trim()).filter(Boolean);
    setChecking(true);
    try {
      const res = await api.post(`/synkly/accounts/${checkNumbersAcc}/check-numbers`, { numbers });
      setCheckResult(res.data.results || []);
    } catch { toast.error('Erro ao verificar números'); }
    finally { setChecking(false); }
  };

  const handleBulkSend = async () => {
    if (!bulkAcc || !bulkRecipients || !bulkText) { toast.error('Preencha todos os campos'); return; }
    const recipients = bulkRecipients.split(/[\n,]/).map(n => n.trim()).filter(Boolean);
    setBulkSending(true);
    try {
      const res = await api.post(`/synkly/accounts/${bulkAcc}/bulk-send`, {
        recipients, text: bulkText,
        minDelayMs: 8000, maxDelayMs: 20000, perHourLimit: 40, perDayLimit: 200, validateNumbers: true
      });
      setBulkJob(res.data);
      toast.success(`Disparo iniciado! Job: ${res.data.jobId}`);
      pollBulkJob(res.data.jobId);
    } catch { toast.error('Erro ao iniciar disparo'); }
    finally { setBulkSending(false); }
  };

  const pollBulkJob = async (jobId) => {
    const interval = setInterval(async () => {
      try {
        const res = await api.get(`/synkly/bulk-send/${jobId}`);
        setJobStatus(res.data);
        if (res.data.status === 'completed' || res.data.status === 'failed') clearInterval(interval);
      } catch { clearInterval(interval); }
    }, 5000);
  };

  const fetchConversations = async () => {
    setConvLoading(true);
    try {
      const res = await api.get(`/synkly/conversations${convAcc ? `?accountId=${convAcc}` : ''}`);
      setConversations(res.data || []);
    } catch { toast.error('Erro ao carregar conversas'); }
    finally { setConvLoading(false); }
  };

  const sections = [
    { id: 'accounts', label: '📱 Contas' },
    { id: 'check', label: '🔍 Verificar Números' },
    { id: 'bulk', label: '📢 Disparo em Massa' },
    { id: 'conversations', label: '💬 Conversas' },
  ];

  return (
    <div className="space-y-4">
      {/* Sub-nav */}
      <div className="flex gap-2 flex-wrap">
        {sections.map(s => (
          <button key={s.id} onClick={() => setActiveSection(s.id)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${activeSection === s.id ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
            {s.label}
          </button>
        ))}
      </div>

      {/* Contas */}
      {activeSection === 'accounts' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-gray-800">Contas WhatsApp conectadas</h3>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={fetchAccounts}>🔄</Button>
              <Button size="sm" onClick={() => setShowCreate(true)} disabled={!configured}>+ Nova conta</Button>
            </div>
          </div>

          {loading ? <p className="text-gray-500 text-sm">Carregando…</p> : whatsappAccounts.length === 0 ? (
            <Card><CardContent className="p-8 text-center text-gray-500">
              <p className="text-3xl mb-2">📱</p>
              <p className="font-medium">Nenhuma conta WhatsApp conectada</p>
              <p className="text-sm mt-1">Clique em "+ Nova conta" para parear via QR code</p>
            </CardContent></Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {whatsappAccounts.map(acc => (
                <Card key={acc.id} className="border border-gray-200">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-semibold text-gray-900">{acc.label}</p>
                        <p className="text-xs text-gray-500 mt-0.5">{acc.externalId || acc.id}</p>
                        <div className="mt-2"><StatusBadge status={acc.status} /></div>
                      </div>
                      <div className="flex flex-col gap-1 items-end">
                        {acc.status === 'qr' && <Button size="sm" variant="outline" onClick={() => handleCheckQr(acc)}>Ver QR</Button>}
                        <Button size="sm" variant="outline" className="text-red-500 border-red-200 hover:bg-red-50" onClick={() => handleDelete(acc.id)}>🗑️</Button>
                      </div>
                    </div>
                    {acc.lastError && <p className="text-xs text-red-500 mt-2">⚠️ {acc.lastError}</p>}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Create modal */}
          <Dialog open={showCreate} onOpenChange={setShowCreate}>
            <DialogContent className="max-w-sm">
              <DialogHeader><DialogTitle>📱 Conectar WhatsApp</DialogTitle></DialogHeader>
              <div className="space-y-3 mt-2">
                <Label>Rótulo da conta</Label>
                <Input placeholder="Ex: Comercial, Suporte…" value={newLabel} onChange={e => setNewLabel(e.target.value)} />
                <p className="text-xs text-gray-500">Após criar, leia o QR code no WhatsApp → Aparelhos conectados.</p>
                <div className="flex justify-end gap-2 pt-2">
                  <Button variant="outline" onClick={() => setShowCreate(false)}>Cancelar</Button>
                  <Button onClick={handleCreate} disabled={creating}>{creating ? 'Criando…' : 'Criar e gerar QR'}</Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          {/* QR modal */}
          <Dialog open={!!qrAccount} onOpenChange={() => setQrAccount(null)}>
            <DialogContent className="max-w-sm text-center">
              <DialogHeader><DialogTitle>📲 Leia o QR code</DialogTitle></DialogHeader>
              <p className="text-sm text-gray-500 mb-4">Abra o WhatsApp → Aparelhos conectados → Conectar aparelho</p>
              {qrAccount?.qr ? (
                <img src={qrAccount.qr} alt="QR WhatsApp" className="mx-auto rounded-xl w-56 h-56 object-contain" />
              ) : (
                <div className="mx-auto w-56 h-56 bg-gray-100 rounded-xl flex items-center justify-center text-gray-400">Gerando QR…</div>
              )}
              {qrPolling && <p className="text-xs text-blue-500 mt-3 animate-pulse">Aguardando confirmação…</p>}
            </DialogContent>
          </Dialog>
        </div>
      )}

      {/* Verificar números */}
      {activeSection === 'check' && (
        <Card><CardContent className="p-5 space-y-4">
          <h3 className="font-semibold text-gray-800">🔍 Verificar números no WhatsApp</h3>
          <div>
            <Label>Conta</Label>
            <select className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" value={checkNumbersAcc} onChange={e => setCheckNumbersAcc(e.target.value)}>
              <option value="">Selecionar conta…</option>
              {whatsappAccounts.filter(a => a.status === 'connected').map(a => <option key={a.id} value={a.id}>{a.label}</option>)}
            </select>
          </div>
          <div>
            <Label>Números (um por linha ou separados por vírgula)</Label>
            <textarea className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2 text-sm h-28" placeholder="5511999999999&#10;5511888888888" value={checkInput} onChange={e => setCheckInput(e.target.value)} />
          </div>
          <Button onClick={handleCheckNumbers} disabled={checking}>{checking ? 'Verificando…' : '🔍 Verificar'}</Button>
          {checkResult && (
            <div className="rounded-lg border overflow-hidden mt-2">
              <table className="w-full text-sm">
                <thead className="bg-gray-50"><tr><th className="text-left px-3 py-2">Número</th><th className="text-left px-3 py-2">Status</th></tr></thead>
                <tbody>{checkResult.map((r, i) => (
                  <tr key={i} className="border-t"><td className="px-3 py-2 font-mono">{r.number}</td>
                  <td className="px-3 py-2">{r.exists ? <span className="text-green-600 font-medium">✅ Cadastrado</span> : <span className="text-red-500">❌ Não cadastrado</span>}</td></tr>
                ))}</tbody>
              </table>
            </div>
          )}
        </CardContent></Card>
      )}

      {/* Disparo em massa */}
      {activeSection === 'bulk' && (
        <div className="space-y-4">
          <Card><CardContent className="p-5 space-y-4">
            <h3 className="font-semibold text-gray-800">📢 Disparo em massa</h3>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label>Conta</Label>
                <select className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" value={bulkAcc} onChange={e => setBulkAcc(e.target.value)}>
                  <option value="">Selecionar conta…</option>
                  {whatsappAccounts.filter(a => a.status === 'connected').map(a => <option key={a.id} value={a.id}>{a.label}</option>)}
                </select>
              </div>
              <div className="col-span-2">
                <Label>Destinatários (um por linha ou vírgula)</Label>
                <textarea className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2 text-sm h-24" placeholder="5511999999999&#10;5511888888888" value={bulkRecipients} onChange={e => setBulkRecipients(e.target.value)} />
              </div>
              <div className="col-span-2">
                <Label>Mensagem</Label>
                <textarea className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2 text-sm h-20" placeholder="Texto da mensagem…" value={bulkText} onChange={e => setBulkText(e.target.value)} />
              </div>
            </div>
            <p className="text-xs text-gray-400">Anti-bloqueio: delay aleatório 8–20s, máx 40/hora, 200/dia, validação de números ativa.</p>
            <Button onClick={handleBulkSend} disabled={bulkSending}>{bulkSending ? 'Enviando…' : '🚀 Iniciar disparo'}</Button>
          </CardContent></Card>

          {(bulkJob || jobStatus) && (
            <Card className="border-blue-200 bg-blue-50"><CardContent className="p-5">
              <h4 className="font-semibold text-blue-900 mb-3">📊 Progresso do disparo</h4>
              {(() => {
                const j = jobStatus || bulkJob;
                return (
                  <div className="space-y-2">
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-medium">Job:</span><code className="text-xs bg-white px-2 py-0.5 rounded border">{j.jobId || j.id}</code>
                      <Badge className={j.status === 'completed' ? 'bg-green-100 text-green-700' : j.status === 'running' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'}>{j.status}</Badge>
                    </div>
                    {j.total !== undefined && (
                      <div className="grid grid-cols-3 gap-3 mt-3">
                        {[['Total', j.total, 'gray'], ['Enviados', j.sent, 'green'], ['Falhas', j.failed, 'red']].map(([l, v, c]) => (
                          <div key={l} className={`bg-white rounded-lg p-3 text-center border border-${c}-200`}>
                            <div className={`text-xl font-bold text-${c}-600`}>{v ?? '-'}</div>
                            <div className="text-xs text-gray-500">{l}</div>
                          </div>
                        ))}
                      </div>
                    )}
                    {j.status === 'running' && <Button size="sm" variant="outline" onClick={() => pollBulkJob(j.id || j.jobId)}>🔄 Atualizar status</Button>}
                  </div>
                );
              })()}
            </CardContent></Card>
          )}
        </div>
      )}

      {/* Conversas */}
      {activeSection === 'conversations' && (
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <select className="border border-gray-200 rounded-lg px-3 py-2 text-sm" value={convAcc} onChange={e => setConvAcc(e.target.value)}>
              <option value="">Todas as contas</option>
              {whatsappAccounts.map(a => <option key={a.id} value={a.id}>{a.label}</option>)}
            </select>
            <Button size="sm" onClick={fetchConversations} disabled={convLoading}>{convLoading ? 'Carregando…' : '🔄 Carregar conversas'}</Button>
          </div>
          {conversations.length === 0 ? (
            <Card><CardContent className="p-6 text-center text-gray-400">
              <p className="text-2xl mb-1">💬</p><p className="text-sm">Nenhuma conversa carregada</p>
            </CardContent></Card>
          ) : (
            <div className="space-y-2">
              {conversations.map(c => (
                <Card key={c.id} className="border border-gray-200 hover:border-green-300 transition-colors cursor-pointer">
                  <CardContent className="p-4 flex items-center justify-between">
                    <div>
                      <p className="font-medium text-gray-900">{c.name || c.externalId}</p>
                      <p className="text-xs text-gray-500 mt-0.5 truncate max-w-xs">{c.lastMessagePreview || '—'}</p>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      {c.unreadCount > 0 && <span className="bg-green-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">{c.unreadCount}</span>}
                      <span className="text-xs text-gray-400">{c.lastMessageAt ? new Date(c.lastMessageAt).toLocaleDateString('pt-BR') : ''}</span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── LinkedIn Tab ─────────────────────────────────────────────────────────────

function LinkedInTab({ api, configured }) {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeSection, setActiveSection] = useState('accounts');

  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({ label: '', cookie: '', userAgent: '' });
  const [creating, setCreating] = useState(false);

  const [connectToken, setConnectToken] = useState(null);
  const [tokenLabel, setTokenLabel] = useState('');
  const [generatingToken, setGeneratingToken] = useState(false);

  const [searchAcc, setSearchAcc] = useState('');
  const [searchForm, setSearchForm] = useState({ keywords: '', title: '', company: '', location: '', limit: 10 });
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);

  const [connectionsAcc, setConnectionsAcc] = useState('');
  const [connectionsKeywords, setConnectionsKeywords] = useState('');
  const [connections, setConnections] = useState(null);
  const [connectionsLoading, setConnectionsLoading] = useState(false);

  const [invAcc, setInvAcc] = useState('');
  const [invitations, setInvitations] = useState([]);
  const [invLoading, setInvLoading] = useState(false);
  const [showInvModal, setShowInvModal] = useState(false);
  const [invForm, setInvForm] = useState({ profileUrn: '', note: '', autoMessage: '' });
  const [invSending, setInvSending] = useState(false);

  const [inmailAcc, setInmailAcc] = useState('');
  const [inmailForm, setInmailForm] = useState({ profileUrn: '', subject: '', body: '' });
  const [inmailSending, setInmailSending] = useState(false);

  const [postAcc, setPostAcc] = useState('');
  const [postForm, setPostForm] = useState({ text: '', visibility: 'anyone' });
  const [postSending, setPostSending] = useState(false);

  const linkedinAccounts = accounts.filter(a => a.channel === 'linkedin');

  const fetchAccounts = async () => {
    if (!configured) return;
    setLoading(true);
    try {
      const res = await api.get('/synkly/accounts');
      setAccounts(res.data || []);
    } catch { toast.error('Erro ao carregar contas'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchAccounts(); }, [configured]);

  const handleCreate = async () => {
    if (!createForm.label || !createForm.cookie) { toast.error('Rótulo e cookie li_at são obrigatórios'); return; }
    setCreating(true);
    try {
      await api.post('/synkly/accounts', { channel: 'linkedin', ...createForm });
      toast.success('Conta LinkedIn conectada!');
      setShowCreate(false); setCreateForm({ label: '', cookie: '', userAgent: '' });
      fetchAccounts();
    } catch { toast.error('Erro ao conectar conta'); }
    finally { setCreating(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remover esta conta LinkedIn?')) return;
    try { await api.delete(`/synkly/accounts/${id}`); toast.success('Conta removida'); fetchAccounts(); }
    catch { toast.error('Erro ao remover'); }
  };

  const handleGenerateToken = async () => {
    setGeneratingToken(true);
    try {
      const res = await api.post('/synkly/accounts/linkedin/connect-token', {});
      setConnectToken(res.data);
    } catch { toast.error('Erro ao gerar token'); }
    finally { setGeneratingToken(false); }
  };

  const handleSearch = async () => {
    if (!searchAcc) { toast.error('Selecione uma conta'); return; }
    setSearching(true);
    try {
      const res = await api.post(`/synkly/accounts/${searchAcc}/linkedin/search`, searchForm);
      setSearchResults(res.data.profiles || []);
    } catch { toast.error('Erro ao buscar perfis'); }
    finally { setSearching(false); }
  };

  const handleConnections = async () => {
    if (!connectionsAcc) { toast.error('Selecione uma conta'); return; }
    setConnectionsLoading(true);
    try {
      const res = await api.post(`/synkly/accounts/${connectionsAcc}/linkedin/connections`, { keywords: connectionsKeywords, count: 50 });
      setConnections(res.data);
    } catch { toast.error('Erro ao listar conexões'); }
    finally { setConnectionsLoading(false); }
  };

  const fetchInvitations = async () => {
    if (!invAcc) { toast.error('Selecione uma conta'); return; }
    setInvLoading(true);
    try {
      const res = await api.get(`/synkly/accounts/${invAcc}/linkedin/invitations`);
      setInvitations(res.data || []);
    } catch { toast.error('Erro ao listar convites'); }
    finally { setInvLoading(false); }
  };

  const handleSendInvitation = async () => {
    if (!invAcc || !invForm.profileUrn) { toast.error('Conta e URN do perfil são obrigatórios'); return; }
    setInvSending(true);
    try {
      await api.post(`/synkly/accounts/${invAcc}/linkedin/invitations`, invForm);
      toast.success('Convite enviado! ✅');
      setShowInvModal(false); setInvForm({ profileUrn: '', note: '', autoMessage: '' });
      fetchInvitations();
    } catch { toast.error('Erro ao enviar convite'); }
    finally { setInvSending(false); }
  };

  const handleInMail = async () => {
    if (!inmailAcc || !inmailForm.profileUrn || !inmailForm.body) { toast.error('Preencha conta, URN e mensagem'); return; }
    setInmailSending(true);
    try {
      await api.post(`/synkly/accounts/${inmailAcc}/linkedin/inmail`, inmailForm);
      toast.success('InMail enviado! ✅');
      setInmailForm({ profileUrn: '', subject: '', body: '' });
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erro ao enviar InMail'); }
    finally { setInmailSending(false); }
  };

  const handlePost = async () => {
    if (!postAcc || !postForm.text) { toast.error('Selecione uma conta e escreva o post'); return; }
    setPostSending(true);
    try {
      const res = await api.post(`/synkly/accounts/${postAcc}/linkedin/post`, postForm);
      toast.success('Post publicado! 🎉');
      if (res.data.postUrl) window.open(res.data.postUrl, '_blank');
      setPostForm({ text: '', visibility: 'anyone' });
    } catch { toast.error('Erro ao publicar post'); }
    finally { setPostSending(false); }
  };

  const INV_STATUS = { pending: { label: 'Pendente', color: 'bg-yellow-100 text-yellow-700' }, accepted: { label: 'Aceito', color: 'bg-green-100 text-green-700' }, declined: { label: 'Recusado', color: 'bg-red-100 text-red-700' }, failed: { label: 'Falha', color: 'bg-red-100 text-red-700' }, withdrawn: { label: 'Retirado', color: 'bg-gray-100 text-gray-600' } };

  const sections = [
    { id: 'accounts', label: '🔵 Contas' },
    { id: 'connections', label: '👥 Conexões' },
    { id: 'search', label: '🔍 Buscar Perfis' },
    { id: 'invitations', label: '✉️ Convites' },
    { id: 'inmail', label: '📨 InMail' },
    { id: 'post', label: '📝 Publicar Post' },
  ];

  return (
    <div className="space-y-4">
      {/* Sub-nav */}
      <div className="flex gap-2 flex-wrap">
        {sections.map(s => (
          <button key={s.id} onClick={() => setActiveSection(s.id)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${activeSection === s.id ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
            {s.label}
          </button>
        ))}
      </div>

      {/* Contas */}
      {activeSection === 'accounts' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-gray-800">Contas LinkedIn conectadas</h3>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={fetchAccounts}>🔄</Button>
              <Button size="sm" onClick={() => setShowCreate(true)} disabled={!configured}>+ Cookie li_at</Button>
            </div>
          </div>

          {loading ? <p className="text-sm text-gray-500">Carregando…</p> : linkedinAccounts.length === 0 ? (
            <Card><CardContent className="p-8 text-center text-gray-500">
              <p className="text-3xl mb-2">🔵</p>
              <p className="font-medium">Nenhuma conta LinkedIn conectada</p>
              <p className="text-sm mt-1">Conecte via cookie li_at ou gere um token para a extensão</p>
            </CardContent></Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {linkedinAccounts.map(acc => (
                <Card key={acc.id} className="border border-gray-200">
                  <CardContent className="p-4 flex items-start justify-between">
                    <div>
                      <p className="font-semibold text-gray-900">{acc.label}</p>
                      <p className="text-xs text-gray-500 mt-0.5">{acc.externalId || acc.id}</p>
                      <div className="mt-2"><StatusBadge status={acc.status} /></div>
                    </div>
                    <Button size="sm" variant="outline" className="text-red-500 border-red-200" onClick={() => handleDelete(acc.id)}>🗑️</Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Token para extensão */}
          <Card className="border-dashed border-blue-200 bg-blue-50/40">
            <CardContent className="p-4 space-y-3">
              <h4 className="font-semibold text-blue-900 text-sm">🔑 Token para extensão de navegador</h4>
              <p className="text-xs text-blue-700">Permite que usuários conectem a própria conta LinkedIn via extensão Synkly sem acesso ao painel.</p>
              <div className="flex gap-2">
                <Button size="sm" onClick={handleGenerateToken} disabled={generatingToken || !configured}>{generatingToken ? 'Gerando…' : 'Gerar token'}</Button>
              </div>
              {connectToken && (
                <div className="bg-white border border-blue-200 rounded-lg p-3">
                  <p className="text-xs text-gray-500 mb-1">Token gerado (uso único, expira em ~10 min):</p>
                  <div className="flex items-center gap-2">
                    <code className="text-xs bg-gray-100 px-2 py-1 rounded flex-1 break-all">{connectToken.token}</code>
                    <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(connectToken.token); toast.success('Copiado!'); }}>📋</Button>
                  </div>
                  <p className="text-xs text-gray-400 mt-1">Expira: {new Date(connectToken.expiresAt).toLocaleString('pt-BR')}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Create modal */}
          <Dialog open={showCreate} onOpenChange={setShowCreate}>
            <DialogContent className="max-w-md">
              <DialogHeader><DialogTitle>🔵 Conectar LinkedIn via cookie</DialogTitle></DialogHeader>
              <div className="space-y-3 mt-2">
                <div><Label>Rótulo *</Label><Input className="mt-1" placeholder="Ex: Prospecção João" value={createForm.label} onChange={e => setCreateForm(f => ({ ...f, label: e.target.value }))} /></div>
                <div>
                  <Label>Cookie li_at *</Label>
                  <Input type="password" className="mt-1" placeholder="AQED..." value={createForm.cookie} onChange={e => setCreateForm(f => ({ ...f, cookie: e.target.value }))} />
                  <p className="text-xs text-gray-400 mt-1">Obtenha em: LinkedIn → DevTools → Application → Cookies → li_at</p>
                </div>
                <div><Label>User-Agent (opcional)</Label><Input className="mt-1" placeholder="Mozilla/5.0…" value={createForm.userAgent} onChange={e => setCreateForm(f => ({ ...f, userAgent: e.target.value }))} /></div>
                <div className="flex justify-end gap-2 pt-2 border-t">
                  <Button variant="outline" onClick={() => setShowCreate(false)}>Cancelar</Button>
                  <Button onClick={handleCreate} disabled={creating}>{creating ? 'Conectando…' : 'Conectar'}</Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      )}

      {/* Conexões 1º grau */}
      {activeSection === 'connections' && (
        <Card><CardContent className="p-5 space-y-4">
          <h3 className="font-semibold text-gray-800">👥 Listar conexões de 1º grau (gratuito)</h3>
          <div className="flex gap-3 flex-wrap">
            <select className="border border-gray-200 rounded-lg px-3 py-2 text-sm" value={connectionsAcc} onChange={e => setConnectionsAcc(e.target.value)}>
              <option value="">Selecionar conta…</option>
              {linkedinAccounts.filter(a => a.status === 'connected').map(a => <option key={a.id} value={a.id}>{a.label}</option>)}
            </select>
            <Input className="text-sm w-48" placeholder="Filtrar por nome/cargo…" value={connectionsKeywords} onChange={e => setConnectionsKeywords(e.target.value)} />
            <Button onClick={handleConnections} disabled={connectionsLoading}>{connectionsLoading ? 'Carregando…' : '🔍 Listar'}</Button>
          </div>
          {connections && (
            <div>
              <p className="text-xs text-gray-500 mb-2">{connections.connections.length} conexões{connections.total ? ` de ${connections.total}` : ''}</p>
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {connections.connections.map((c, i) => (
                  <div key={i} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2">
                    <div>
                      <p className="text-sm font-medium text-gray-900">{c.name || '—'}</p>
                      <p className="text-xs text-gray-500">{c.headline || ''}</p>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <code className="text-xs bg-white border rounded px-1 text-gray-400 max-w-[120px] truncate">{c.urn}</code>
                      <Button size="sm" variant="ghost" className="text-xs h-6 px-2" onClick={() => { navigator.clipboard.writeText(c.urn); toast.success('URN copiado'); }}>📋 URN</Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent></Card>
      )}

      {/* Buscar perfis */}
      {activeSection === 'search' && (
        <Card><CardContent className="p-5 space-y-4">
          <h3 className="font-semibold text-gray-800">🔍 Buscar perfis LinkedIn</h3>
          <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">⚠️ Requer LinkedIn Premium ou Sales Navigator para melhores resultados.</p>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label>Conta</Label>
              <select className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" value={searchAcc} onChange={e => setSearchAcc(e.target.value)}>
                <option value="">Selecionar conta…</option>
                {linkedinAccounts.filter(a => a.status === 'connected').map(a => <option key={a.id} value={a.id}>{a.label}</option>)}
              </select>
            </div>
            {[['keywords', 'Palavras-chave', 'gerente de marketing'], ['title', 'Cargo', 'CMO'], ['company', 'Empresa', 'Acme'], ['location', 'Localização', 'São Paulo']].map(([k, label, ph]) => (
              <div key={k}>
                <Label className="text-xs">{label}</Label>
                <Input className="mt-1 text-sm" placeholder={ph} value={searchForm[k]} onChange={e => setSearchForm(f => ({ ...f, [k]: e.target.value }))} />
              </div>
            ))}
            <div>
              <Label className="text-xs">Limite</Label>
              <Input type="number" min="1" max="50" className="mt-1 text-sm" value={searchForm.limit} onChange={e => setSearchForm(f => ({ ...f, limit: Number(e.target.value) }))} />
            </div>
          </div>
          <Button onClick={handleSearch} disabled={searching}>{searching ? 'Buscando…' : '🔍 Buscar'}</Button>
          {searchResults !== null && (
            <div>
              <p className="text-xs text-gray-500 mb-2">{searchResults.length} perfis encontrados</p>
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {searchResults.length === 0 ? <p className="text-sm text-gray-400 text-center py-4">Nenhum resultado</p> :
                  searchResults.map((p, i) => (
                    <div key={i} className="bg-gray-50 rounded-lg px-3 py-2 flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium">{p.name}</p>
                        <p className="text-xs text-gray-500">{p.headline}{p.location ? ` · ${p.location}` : ''}</p>
                      </div>
                      <div className="flex gap-1">
                        {p.profileUrl && <a href={p.profileUrl} target="_blank" rel="noreferrer" className="text-xs text-blue-600 hover:underline">Ver perfil</a>}
                        <Button size="sm" variant="ghost" className="text-xs h-6 px-2" onClick={() => { navigator.clipboard.writeText(p.urn); toast.success('URN copiado'); }}>📋</Button>
                      </div>
                    </div>
                  ))
                }
              </div>
            </div>
          )}
        </CardContent></Card>
      )}

      {/* Convites */}
      {activeSection === 'invitations' && (
        <div className="space-y-4">
          <div className="flex items-center gap-3 flex-wrap">
            <select className="border border-gray-200 rounded-lg px-3 py-2 text-sm" value={invAcc} onChange={e => setInvAcc(e.target.value)}>
              <option value="">Selecionar conta…</option>
              {linkedinAccounts.filter(a => a.status === 'connected').map(a => <option key={a.id} value={a.id}>{a.label}</option>)}
            </select>
            <Button size="sm" variant="outline" onClick={fetchInvitations} disabled={invLoading}>{invLoading ? '…' : '🔄 Listar convites'}</Button>
            <Button size="sm" onClick={() => setShowInvModal(true)} disabled={!invAcc}>✉️ Enviar convite</Button>
          </div>
          {invitations.length > 0 && (
            <div className="space-y-2">
              {invitations.map((inv, i) => {
                const s = INV_STATUS[inv.status] || { label: inv.status, color: 'bg-gray-100 text-gray-600' };
                return (
                  <Card key={i} className="border border-gray-200">
                    <CardContent className="p-4 flex items-center justify-between">
                      <div>
                        <p className="font-medium text-sm">{inv.profileName || inv.profileUrn}</p>
                        {inv.note && <p className="text-xs text-gray-500 mt-0.5">"{inv.note}"</p>}
                        {inv.autoMessage && <p className="text-xs text-blue-500 mt-0.5">Auto-msg: {inv.autoMessage}</p>}
                      </div>
                      <span className={`text-xs font-semibold px-2 py-1 rounded-full ${s.color}`}>{s.label}</span>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
          <Dialog open={showInvModal} onOpenChange={setShowInvModal}>
            <DialogContent className="max-w-md">
              <DialogHeader><DialogTitle>✉️ Enviar convite de conexão</DialogTitle></DialogHeader>
              <div className="space-y-3 mt-2">
                <div><Label>URN do perfil *</Label><Input className="mt-1 font-mono text-xs" placeholder="urn:li:fsd_profile:ACoAAB…" value={invForm.profileUrn} onChange={e => setInvForm(f => ({ ...f, profileUrn: e.target.value }))} /></div>
                <div><Label>Nota (opcional)</Label><textarea className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2 text-sm h-20" placeholder="Olá, vamos nos conectar?" value={invForm.note} onChange={e => setInvForm(f => ({ ...f, note: e.target.value }))} /></div>
                <div><Label>Mensagem automática ao aceitar</Label><textarea className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2 text-sm h-20" placeholder="Obrigado por aceitar! Posso te apresentar…" value={invForm.autoMessage} onChange={e => setInvForm(f => ({ ...f, autoMessage: e.target.value }))} /></div>
                <div className="flex justify-end gap-2 pt-2 border-t">
                  <Button variant="outline" onClick={() => setShowInvModal(false)}>Cancelar</Button>
                  <Button onClick={handleSendInvitation} disabled={invSending}>{invSending ? 'Enviando…' : 'Enviar convite'}</Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      )}

      {/* InMail */}
      {activeSection === 'inmail' && (
        <Card><CardContent className="p-5 space-y-4">
          <h3 className="font-semibold text-gray-800">📨 Enviar InMail</h3>
          <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">⚠️ Requer LinkedIn Premium/Sales Navigator com créditos de InMail.</p>
          <div className="space-y-3">
            <div><Label>Conta</Label>
              <select className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" value={inmailAcc} onChange={e => setInmailAcc(e.target.value)}>
                <option value="">Selecionar…</option>{linkedinAccounts.filter(a => a.status === 'connected').map(a => <option key={a.id} value={a.id}>{a.label}</option>)}
              </select></div>
            <div><Label>URN do perfil *</Label><Input className="mt-1 font-mono text-xs" placeholder="urn:li:fsd_profile:ACoAAB…" value={inmailForm.profileUrn} onChange={e => setInmailForm(f => ({ ...f, profileUrn: e.target.value }))} /></div>
            <div><Label>Assunto</Label><Input className="mt-1" placeholder="Oportunidade de parceria" value={inmailForm.subject} onChange={e => setInmailForm(f => ({ ...f, subject: e.target.value }))} /></div>
            <div><Label>Mensagem *</Label><textarea className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2 text-sm h-24" placeholder="Olá, gostaria de conversar sobre…" value={inmailForm.body} onChange={e => setInmailForm(f => ({ ...f, body: e.target.value }))} /></div>
            <Button onClick={handleInMail} disabled={inmailSending}>{inmailSending ? 'Enviando…' : '📨 Enviar InMail'}</Button>
          </div>
        </CardContent></Card>
      )}

      {/* Post no feed */}
      {activeSection === 'post' && (
        <Card><CardContent className="p-5 space-y-4">
          <h3 className="font-semibold text-gray-800">📝 Publicar no feed LinkedIn</h3>
          <p className="text-xs text-gray-400">Best-effort via API não oficial. A publicação pode falhar se o LinkedIn aplicar restrições.</p>
          <div className="space-y-3">
            <div><Label>Conta</Label>
              <select className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" value={postAcc} onChange={e => setPostAcc(e.target.value)}>
                <option value="">Selecionar…</option>{linkedinAccounts.filter(a => a.status === 'connected').map(a => <option key={a.id} value={a.id}>{a.label}</option>)}
              </select></div>
            <div><Label>Texto do post *</Label><textarea className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2 text-sm h-32" placeholder="Escreva seu post aqui…" value={postForm.text} onChange={e => setPostForm(f => ({ ...f, text: e.target.value }))} /></div>
            <div><Label>Visibilidade</Label>
              <select className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" value={postForm.visibility} onChange={e => setPostForm(f => ({ ...f, visibility: e.target.value }))}>
                <option value="anyone">🌎 Público (todos)</option>
                <option value="connections">👥 Apenas conexões</option>
              </select></div>
            <Button onClick={handlePost} disabled={postSending}>{postSending ? 'Publicando…' : '🚀 Publicar'}</Button>
          </div>
        </CardContent></Card>
      )}
    </div>
  );
}

// ─── Twilio Integration Tab ───────────────────────────────────────────────────

function TwilioTab({ api }) {
  const [cfg, setCfg] = useState({ twilio_account_sid: '', twilio_auth_token: '', twilio_phone_number: '', twilio_sms_number: '' });
  const [showToken, setShowToken] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/integrations/twilio').then(r => {
      setCfg(prev => ({ ...prev, ...r.data }));
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const save = async () => {
    if (!cfg.twilio_account_sid || !cfg.twilio_auth_token) { toast.error('Account SID e Auth Token são obrigatórios'); return; }
    setSaving(true);
    try {
      await api.post('/integrations/twilio', cfg);
      toast.success('Twilio salvo com sucesso!');
    } catch { toast.error('Erro ao salvar'); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    if (!window.confirm('Remover configuração Twilio?')) return;
    try {
      await api.delete('/integrations/twilio');
      setCfg({ twilio_account_sid: '', twilio_auth_token: '', twilio_phone_number: '', twilio_sms_number: '' });
      toast.success('Configuração removida');
    } catch { toast.error('Erro ao remover'); }
  };

  if (loading) return <div className="py-10 text-center text-gray-400"><span className="animate-spin mr-2">⏳</span>Carregando…</div>;
  const configured = cfg.configured;

  return (
    <div className="space-y-5">
      <div className={`flex items-center justify-between px-4 py-3 rounded-xl border ${configured ? 'bg-green-50 border-green-200' : 'bg-orange-50 border-orange-200'}`}>
        <div className="flex items-center gap-3">
          <span className="text-2xl">📞</span>
          <div>
            <p className={`text-sm font-semibold ${configured ? 'text-green-800' : 'text-orange-800'}`}>{configured ? '✅ Twilio conectado' : '⚠️ Twilio não configurado'}</p>
            <p className={`text-xs ${configured ? 'text-green-600' : 'text-orange-600'}`}>{configured ? `Número voz: ${cfg.twilio_phone_number || '—'}  |  SMS: ${cfg.twilio_sms_number || '—'}` : 'Configure para ligações de voz (Maya SDR) e envio de SMS (UniqueChannel)'}</p>
          </div>
        </div>
        {configured && <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={remove}>🗑️ Remover</Button>}
      </div>

      <Card>
        <CardContent className="pt-5 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Account SID *</Label>
              <Input placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx" className="font-mono text-sm"
                value={cfg.twilio_account_sid || ''} onChange={e => setCfg(c => ({ ...c, twilio_account_sid: e.target.value }))} />
              <p className="text-xs text-gray-400">console.twilio.com → Dashboard</p>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Auth Token *</Label>
              <div className="relative">
                <Input type={showToken ? 'text' : 'password'} className="font-mono text-sm pr-10"
                  placeholder="••••••••••••••••••••••••••••••••"
                  value={cfg.twilio_auth_token || ''} onChange={e => setCfg(c => ({ ...c, twilio_auth_token: e.target.value }))} />
                <button type="button" onClick={() => setShowToken(v => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">{showToken ? '🙈' : '👁️'}</button>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Número para Ligações (VoIP)</Label>
              <Input placeholder="+5511XXXXXXXXX" className="font-mono text-sm"
                value={cfg.twilio_phone_number || ''} onChange={e => setCfg(c => ({ ...c, twilio_phone_number: e.target.value }))} />
              <p className="text-xs text-gray-400">Formato E.164 — usado pelo Maya SDR para ligações</p>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Número para SMS</Label>
              <Input placeholder="+5511XXXXXXXXX" className="font-mono text-sm"
                value={cfg.twilio_sms_number || ''} onChange={e => setCfg(c => ({ ...c, twilio_sms_number: e.target.value }))} />
              <p className="text-xs text-gray-400">Pode ser o mesmo número ou um dedicado para SMS</p>
            </div>
          </div>
          <div className="p-3 rounded-lg bg-blue-50 border border-blue-100 text-xs text-blue-700 space-y-1">
            <p className="font-semibold">Como obter:</p>
            <p>1. Crie conta em <span className="font-mono">console.twilio.com</span></p>
            <p>2. Copie Account SID e Auth Token do Dashboard principal</p>
            <p>3. Compre um número BR em Phone Numbers → Buy a number (compatível com SMS + Voz)</p>
          </div>
          <div className="flex justify-end">
            <Button onClick={save} disabled={saving} className="btn-primary">{saving ? 'Salvando…' : '💾 Salvar Twilio'}</Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ─── ElevenLabs Integration Tab ───────────────────────────────────────────────

function GroqTab({ api }) {
  const [cfg, setCfg] = useState({ groq_api_key: '', configured: false, api_key_set: false, source: 'none', model: 'openai/gpt-oss-120b' });
  const [showKey, setShowKey] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/integrations/groq').then(r => setCfg(prev => ({ ...prev, ...r.data }))).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const save = async () => {
    if (!cfg.groq_api_key) { toast.error('API Key é obrigatória'); return; }
    setSaving(true);
    try {
      await api.post('/integrations/groq', { groq_api_key: cfg.groq_api_key });
      setCfg(c => ({ ...c, configured: true, api_key_set: true, source: 'integrations' }));
      toast.success('Groq salvo com sucesso!');
    } catch { toast.error('Erro ao salvar'); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    if (!window.confirm('Remover configuração Groq?')) return;
    try {
      await api.delete('/integrations/groq');
      setCfg({ groq_api_key: '', configured: false, api_key_set: false, source: 'none', model: cfg.model });
      toast.success('Configuração removida');
    } catch { toast.error('Erro ao remover'); }
  };

  if (loading) return <div className="py-10 text-center text-gray-400"><span className="animate-spin mr-2">⏳</span>Carregando…</div>;
  const configured = cfg.configured;

  return (
    <div className="space-y-5">
      <div className={`flex items-center justify-between px-4 py-3 rounded-xl border ${configured ? 'bg-green-50 border-green-200' : 'bg-orange-50 border-orange-200'}`}>
        <div className="flex items-center gap-3">
          <span className="text-2xl">🧠</span>
          <div>
            <p className={`text-sm font-semibold ${configured ? 'text-green-800' : 'text-orange-800'}`}>{configured ? '✅ Groq configurado' : '⚠️ Groq não configurado'}</p>
            <p className={`text-xs ${configured ? 'text-green-600' : 'text-orange-600'}`}>
              {configured ? `Modelo padrão: ${cfg.model} · origem: ${cfg.source === 'integrations' ? 'Integrações Gerais' : '.env'}` : 'Configure para campanhas, legendas e geração textual com IA'}
            </p>
          </div>
        </div>
        {configured && <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={remove}>🗑️ Remover</Button>}
      </div>

      <Card>
        <CardContent className="pt-5 space-y-4">
          <div className="space-y-1.5 max-w-md">
            <Label className="text-xs font-medium">API Key *</Label>
            <div className="relative">
              <Input type={showKey ? 'text' : 'password'} className="font-mono text-sm pr-10"
                placeholder="gsk_••••••••••••••••••••••••"
                value={cfg.groq_api_key || ''} onChange={e => setCfg(c => ({ ...c, groq_api_key: e.target.value }))} />
              <button type="button" onClick={() => setShowKey(v => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">{showKey ? '🙈' : '👁️'}</button>
            </div>
            <p className="text-xs text-gray-400">console.groq.com → API Keys</p>
          </div>

          <div className="p-3 rounded-lg bg-violet-50 border border-violet-100 text-xs text-violet-700 space-y-1">
            <p className="font-semibold">Usado em:</p>
            <p>Campanhas com IA, geração de legendas, copy de criativos e outros fluxos textuais do NexoSynX.</p>
          </div>

          <div className="flex items-center justify-between">
            <Button variant="outline" onClick={() => window.open('https://console.groq.com', '_blank')}>🔗 Abrir Groq</Button>
            <Button onClick={save} disabled={saving} className="btn-primary">{saving ? 'Salvando…' : '💾 Salvar Groq'}</Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function PexelsTab({ api }) {
  const [cfg, setCfg] = useState({ pexels_api_key: '', configured: false, api_key_set: false, source: 'none' });
  const [showKey, setShowKey] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/integrations/pexels').then(r => setCfg(prev => ({ ...prev, ...r.data }))).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const save = async () => {
    if (!cfg.pexels_api_key) { toast.error('API Key é obrigatória'); return; }
    setSaving(true);
    try {
      await api.post('/integrations/pexels', { pexels_api_key: cfg.pexels_api_key });
      setCfg(c => ({ ...c, configured: true, api_key_set: true, source: 'integrations' }));
      toast.success('Pexels salvo com sucesso!');
    } catch { toast.error('Erro ao salvar'); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    if (!window.confirm('Remover configuração Pexels?')) return;
    try {
      await api.delete('/integrations/pexels');
      setCfg({ pexels_api_key: '', configured: false, api_key_set: false, source: 'none' });
      toast.success('Configuração removida');
    } catch { toast.error('Erro ao remover'); }
  };

  if (loading) return <div className="py-10 text-center text-gray-400"><span className="animate-spin mr-2">⏳</span>Carregando…</div>;
  const configured = cfg.configured;

  return (
    <div className="space-y-5">
      <div className={`flex items-center justify-between px-4 py-3 rounded-xl border ${configured ? 'bg-green-50 border-green-200' : 'bg-orange-50 border-orange-200'}`}>
        <div className="flex items-center gap-3">
          <span className="text-2xl">🖼️</span>
          <div>
            <p className={`text-sm font-semibold ${configured ? 'text-green-800' : 'text-orange-800'}`}>{configured ? '✅ Pexels configurado' : '⚠️ Pexels não configurado'}</p>
            <p className={`text-xs ${configured ? 'text-green-600' : 'text-orange-600'}`}>
              {configured ? `Origem: ${cfg.source === 'integrations' ? 'Integrações Gerais' : '.env'}` : 'Configure para busca de imagens de apoio nos criativos'}
            </p>
          </div>
        </div>
        {configured && <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={remove}>🗑️ Remover</Button>}
      </div>

      <Card>
        <CardContent className="pt-5 space-y-4">
          <div className="space-y-1.5 max-w-md">
            <Label className="text-xs font-medium">API Key *</Label>
            <div className="relative">
              <Input type={showKey ? 'text' : 'password'} className="font-mono text-sm pr-10"
                placeholder="563492ad6f91700001000001••••"
                value={cfg.pexels_api_key || ''} onChange={e => setCfg(c => ({ ...c, pexels_api_key: e.target.value }))} />
              <button type="button" onClick={() => setShowKey(v => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">{showKey ? '🙈' : '👁️'}</button>
            </div>
            <p className="text-xs text-gray-400">pexels.com/api → Your API Key</p>
          </div>

          <div className="p-3 rounded-lg bg-sky-50 border border-sky-100 text-xs text-sky-700 space-y-1">
            <p className="font-semibold">Usado em:</p>
            <p>Busca de imagens base para criativos automáticos do NexoHub.</p>
          </div>

          <div className="flex items-center justify-between">
            <Button variant="outline" onClick={() => window.open('https://www.pexels.com/api/', '_blank')}>🔗 Abrir Pexels</Button>
            <Button onClick={save} disabled={saving} className="btn-primary">{saving ? 'Salvando…' : '💾 Salvar Pexels'}</Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function MusicAPITab({ api }) {
  const [cfg, setCfg] = useState({ musicapi_api_key: '', configured: false, api_key_set: false, source: 'none' });
  const [showKey, setShowKey] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/integrations/musicapi').then(r => setCfg(prev => ({ ...prev, ...r.data }))).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const save = async () => {
    if (!cfg.musicapi_api_key) { toast.error('API Key é obrigatória'); return; }
    setSaving(true);
    try {
      await api.post('/integrations/musicapi', { musicapi_api_key: cfg.musicapi_api_key });
      setCfg(c => ({ ...c, configured: true, api_key_set: true, source: 'integrations' }));
      toast.success('MusicAPI salvo com sucesso!');
    } catch { toast.error('Erro ao salvar'); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    if (!window.confirm('Remover configuração MusicAPI?')) return;
    try {
      await api.delete('/integrations/musicapi');
      setCfg({ musicapi_api_key: '', configured: false, api_key_set: false, source: 'none' });
      toast.success('Configuração removida');
    } catch { toast.error('Erro ao remover'); }
  };

  if (loading) return <div className="py-10 text-center text-gray-400"><span className="animate-spin mr-2">⏳</span>Carregando…</div>;
  const configured = cfg.configured;

  return (
    <div className="space-y-5">
      <div className={`flex items-center justify-between px-4 py-3 rounded-xl border ${configured ? 'bg-green-50 border-green-200' : 'bg-orange-50 border-orange-200'}`}>
        <div className="flex items-center gap-3">
          <span className="text-2xl">🎼</span>
          <div>
            <p className={`text-sm font-semibold ${configured ? 'text-green-800' : 'text-orange-800'}`}>{configured ? '✅ MusicAPI configurado' : '⚠️ MusicAPI não configurado'}</p>
            <p className={`text-xs ${configured ? 'text-green-600' : 'text-orange-600'}`}>
              {configured ? `Origem: ${cfg.source === 'integrations' ? 'Integrações Gerais' : '.env'}` : 'Configure para geração de jingles e músicas no NexoHub'}
            </p>
          </div>
        </div>
        {configured && <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={remove}>🗑️ Remover</Button>}
      </div>

      <Card>
        <CardContent className="pt-5 space-y-4">
          <div className="space-y-1.5 max-w-md">
            <Label className="text-xs font-medium">API Key *</Label>
            <div className="relative">
              <Input type={showKey ? 'text' : 'password'} className="font-mono text-sm pr-10"
                placeholder="ma_••••••••••••••••••••••••"
                value={cfg.musicapi_api_key || ''} onChange={e => setCfg(c => ({ ...c, musicapi_api_key: e.target.value }))} />
              <button type="button" onClick={() => setShowKey(v => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">{showKey ? '🙈' : '👁️'}</button>
            </div>
            <p className="text-xs text-gray-400">dashboard.musicapi.ai ou musicapi.ai → API Keys</p>
          </div>

          <div className="p-3 rounded-lg bg-amber-50 border border-amber-100 text-xs text-amber-800 space-y-1">
            <p className="font-semibold">Usado em:</p>
            <p>Geração de músicas instrumentais e jingles do NexoHub. Instrumental usa Producer/Lyria 3 Pro; faixas com vocal usam Sonic.</p>
          </div>

          <div className="flex items-center justify-between">
            <Button variant="outline" onClick={() => window.open('https://musicapi.ai/docs', '_blank')}>🔗 Abrir MusicAPI</Button>
            <Button onClick={save} disabled={saving} className="btn-primary">{saving ? 'Salvando…' : '💾 Salvar MusicAPI'}</Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function ElevenLabsTab({ api }) {
  const [cfg, setCfg] = useState({ elevenlabs_api_key: '', elevenlabs_voice_id: '', elevenlabs_voice_name: '' });
  const [showKey, setShowKey] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/integrations/elevenlabs').then(r => {
      setCfg(prev => ({ ...prev, ...r.data }));
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const save = async () => {
    if (!cfg.elevenlabs_api_key) { toast.error('API Key é obrigatória'); return; }
    setSaving(true);
    try {
      await api.post('/integrations/elevenlabs', cfg);
      toast.success('ElevenLabs salvo com sucesso!');
    } catch { toast.error('Erro ao salvar'); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    if (!window.confirm('Remover configuração ElevenLabs?')) return;
    try {
      await api.delete('/integrations/elevenlabs');
      setCfg({ elevenlabs_api_key: '', elevenlabs_voice_id: '', elevenlabs_voice_name: '' });
      toast.success('Configuração removida');
    } catch { toast.error('Erro ao remover'); }
  };

  if (loading) return <div className="py-10 text-center text-gray-400"><span className="animate-spin mr-2">⏳</span>Carregando…</div>;
  const configured = cfg.configured;

  return (
    <div className="space-y-5">
      <div className={`flex items-center justify-between px-4 py-3 rounded-xl border ${configured ? 'bg-green-50 border-green-200' : 'bg-orange-50 border-orange-200'}`}>
        <div className="flex items-center gap-3">
          <span className="text-2xl">🎙️</span>
          <div>
            <p className={`text-sm font-semibold ${configured ? 'text-green-800' : 'text-orange-800'}`}>{configured ? '✅ ElevenLabs conectado' : '⚠️ ElevenLabs não configurado'}</p>
            <p className={`text-xs ${configured ? 'text-green-600' : 'text-orange-600'}`}>{configured ? `Voz padrão: ${cfg.elevenlabs_voice_name || cfg.elevenlabs_voice_id || '—'}` : 'Configure para síntese de voz no Maya SDR'}</p>
          </div>
        </div>
        {configured && <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={remove}>🗑️ Remover</Button>}
      </div>

      <Card>
        <CardContent className="pt-5 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5 md:col-span-2">
              <Label className="text-xs font-medium">API Key *</Label>
              <div className="relative max-w-md">
                <Input type={showKey ? 'text' : 'password'} className="font-mono text-sm pr-10"
                  placeholder="sk_••••••••••••••••••••••••••••••••"
                  value={cfg.elevenlabs_api_key || ''} onChange={e => setCfg(c => ({ ...c, elevenlabs_api_key: e.target.value }))} />
                <button type="button" onClick={() => setShowKey(v => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">{showKey ? '🙈' : '👁️'}</button>
              </div>
              <p className="text-xs text-gray-400">elevenlabs.io → Profile Settings → API Key</p>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Voice ID padrão</Label>
              <Input placeholder="pNInz6obpgDQGcFmaJgB" className="font-mono text-sm"
                value={cfg.elevenlabs_voice_id || ''} onChange={e => setCfg(c => ({ ...c, elevenlabs_voice_id: e.target.value }))} />
              <p className="text-xs text-gray-400">Voice Library → escolha a voz → copie o ID</p>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Nome da Voz (referência)</Label>
              <Input placeholder="Ex: Aria — Português BR"
                value={cfg.elevenlabs_voice_name || ''} onChange={e => setCfg(c => ({ ...c, elevenlabs_voice_name: e.target.value }))} />
            </div>
          </div>
          <div className="p-3 rounded-lg bg-purple-50 border border-purple-100 text-xs text-purple-700 space-y-1">
            <p className="font-semibold">Como obter:</p>
            <p>1. Crie conta em <span className="font-mono">elevenlabs.io</span> (plano Starter, ~$5/mês)</p>
            <p>2. Profile Settings → copie a API Key</p>
            <p>3. Voice Library → escolha voz em português → copie Voice ID</p>
            <p>4. Vozes PT-BR recomendadas: <strong>Aria, Nicole, Rachel</strong></p>
          </div>
          <div className="flex justify-end">
            <Button onClick={save} disabled={saving} className="btn-primary">{saving ? 'Salvando…' : '💾 Salvar ElevenLabs'}</Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ─── SMTP Integration Tab ─────────────────────────────────────────────────────

const SMTP_PRESETS = [
  { label: 'Gmail',           host: 'smtp.gmail.com',      port: 587, tls: true,  ssl: false },
  { label: 'Outlook / Hotmail', host: 'smtp.office365.com', port: 587, tls: true,  ssl: false },
  { label: 'Yahoo',           host: 'smtp.mail.yahoo.com', port: 587, tls: true,  ssl: false },
  { label: 'Zoho Mail',       host: 'smtp.zoho.com',       port: 587, tls: true,  ssl: false },
  { label: 'SendGrid',        host: 'smtp.sendgrid.net',   port: 587, tls: true,  ssl: false },
  { label: 'Amazon SES',      host: 'email-smtp.us-east-1.amazonaws.com', port: 587, tls: true, ssl: false },
  { label: 'Personalizado',   host: '',                    port: 587, tls: true,  ssl: false },
];

function SMTPTab({ api }) {
  const [cfg, setCfg] = useState({
    smtp_host: '', smtp_port: 587, smtp_username: '',
    smtp_password: '', smtp_from_name: '', smtp_from_email: '',
    smtp_use_tls: true, smtp_use_ssl: false,
  });
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    api.get('/integrations/smtp').then(r => {
      setCfg(prev => ({ ...prev, ...r.data }));
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const applyPreset = (preset) => {
  if (preset.host !== '') {
    setCfg(c => ({ ...c, smtp_host: preset.host, smtp_port: preset.port, smtp_use_tls: preset.tls, smtp_use_ssl: preset.ssl }));
  } else {
    setCfg(c => ({ ...c, smtp_host: '', smtp_port: preset.port, smtp_use_tls: preset.tls, smtp_use_ssl: preset.ssl }));
  }
};
 
  const save = async () => {
    if (!cfg.smtp_host) { toast.error('Host SMTP é obrigatório'); return; }
    if (!cfg.smtp_username) { toast.error('Usuário / e-mail é obrigatório'); return; }
    if (!cfg.smtp_password && !cfg.password_set) { toast.error('Senha / App Password é obrigatória'); return; }
    setSaving(true);
    try {
      await api.post('/integrations/smtp', cfg);
      toast.success('Configuração SMTP salva!');
      const r = await api.get('/integrations/smtp');
      setCfg(prev => ({ ...prev, ...r.data }));
    } catch { toast.error('Erro ao salvar'); }
    finally { setSaving(false); }
  };

  const testConnection = async () => {
    setTesting(true);
    try {
      const r = await api.post('/integrations/smtp/test');
      toast.success(r.data.message || 'Conexão bem-sucedida!');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Falha na conexão SMTP');
    } finally { setTesting(false); }
  };

  const remove = async () => {
    if (!window.confirm('Remover configuração SMTP?')) return;
    try {
      await api.delete('/integrations/smtp');
      setCfg({ smtp_host: '', smtp_port: 587, smtp_username: '', smtp_password: '', smtp_from_name: '', smtp_from_email: '', smtp_use_tls: true, smtp_use_ssl: false });
      toast.success('Configuração removida');
    } catch { toast.error('Erro ao remover'); }
  };

  if (loading) return <div className="py-10 text-center text-gray-400"><span className="animate-spin mr-2">⏳</span>Carregando…</div>;
  const configured = cfg.configured;

  return (
    <div className="space-y-5">
      {/* Status banner */}
      <div className={`flex items-center justify-between px-4 py-3 rounded-xl border ${configured ? 'bg-green-50 border-green-200' : 'bg-orange-50 border-orange-200'}`}>
        <div className="flex items-center gap-3">
          <span className="text-2xl">📧</span>
          <div>
            <p className={`text-sm font-semibold ${configured ? 'text-green-800' : 'text-orange-800'}`}>
              {configured ? '✅ SMTP configurado' : '⚠️ SMTP não configurado'}
            </p>
            <p className={`text-xs ${configured ? 'text-green-600' : 'text-orange-600'}`}>
              {configured
                ? `${cfg.smtp_host}:${cfg.smtp_port} · De: ${cfg.smtp_from_name || cfg.smtp_username}`
                : 'Configure para envio de e-mails transacionais pelo NexoSynX'}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          {configured && (
            <Button size="sm" variant="outline" className="text-blue-600 border-blue-200 hover:bg-blue-50"
              onClick={testConnection} disabled={testing}>
              {testing ? '⏳ Testando…' : '🔌 Testar Conexão'}
            </Button>
          )}
          {configured && (
            <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={remove}>
              🗑️ Remover
            </Button>
          )}
        </div>
      </div>

      {/* Presets */}
      <div className="space-y-1.5">
        <Label className="text-xs font-medium text-gray-500">Provedor de e-mail</Label>
        <div className="flex flex-wrap gap-2">
          {SMTP_PRESETS.map(p => (
            <button key={p.label} onClick={() => applyPreset(p)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all hover:shadow-sm
                ${cfg.smtp_host === p.host && p.host !== ''
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-white text-gray-600 border-gray-200 hover:border-blue-300'}`}>
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* Fields */}
      <Card>
        <CardContent className="pt-5 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Host */}
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Host SMTP *</Label>
              <Input placeholder="smtp.gmail.com" className="font-mono text-sm"
                value={cfg.smtp_host || ''} onChange={e => setCfg(c => ({ ...c, smtp_host: e.target.value }))} />
            </div>
            {/* Port */}
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Porta *</Label>
              <Input type="number" placeholder="587" className="font-mono text-sm"
                value={cfg.smtp_port || ''} onChange={e => setCfg(c => ({ ...c, smtp_port: Number(e.target.value) }))} />
              <p className="text-xs text-gray-400">587 (TLS) · 465 (SSL) · 25 (sem criptografia)</p>
            </div>
            {/* Username */}
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Usuário / E-mail *</Label>
              <Input placeholder="seu@email.com" className="font-mono text-sm"
                value={cfg.smtp_username || ''} onChange={e => setCfg(c => ({ ...c, smtp_username: e.target.value }))} />
            </div>
            {/* Password */}
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">{cfg.password_set ? 'Nova Senha (deixe vazio para manter)' : 'Senha / App Password *'}</Label>
              <div className="relative">
                <Input type={showPass ? 'text' : 'password'} className="font-mono text-sm pr-10"
                  placeholder={cfg.password_set ? '••••••••• (já configurada)' : '••••••••••••••••'}
                  value={cfg.smtp_password || ''} onChange={e => setCfg(c => ({ ...c, smtp_password: e.target.value }))} />
                <button type="button" onClick={() => setShowPass(v => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                  {showPass ? '🙈' : '👁️'}
                </button>
              </div>
            </div>
            {/* From Name */}
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Nome do Remetente</Label>
              <Input placeholder="NexoSynX CRM"
                value={cfg.smtp_from_name || ''} onChange={e => setCfg(c => ({ ...c, smtp_from_name: e.target.value }))} />
              <p className="text-xs text-gray-400">Nome exibido no campo "De:" dos e-mails</p>
            </div>
            {/* From Email */}
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">E-mail do Remetente</Label>
              <Input placeholder="noreply@suaempresa.com" className="font-mono text-sm"
                value={cfg.smtp_from_email || ''} onChange={e => setCfg(c => ({ ...c, smtp_from_email: e.target.value }))} />
              <p className="text-xs text-gray-400">Se vazio, usa o campo Usuário acima</p>
            </div>
          </div>

          {/* TLS / SSL toggles */}
          <div className="flex flex-wrap gap-6 pt-1">
            <label className="flex items-center gap-2 cursor-pointer">
              <div
                onClick={() => setCfg(c => ({ ...c, smtp_use_tls: !c.smtp_use_tls, smtp_use_ssl: false }))}
                className={`w-10 h-6 rounded-full flex items-center transition-all px-0.5 ${cfg.smtp_use_tls ? 'bg-blue-500' : 'bg-gray-200'}`}>
                <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform ${cfg.smtp_use_tls ? 'translate-x-4' : 'translate-x-0'}`} />
              </div>
              <span className="text-sm font-medium text-gray-700">STARTTLS</span>
              <span className="text-xs text-gray-400">(recomendado para porta 587)</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <div
                onClick={() => setCfg(c => ({ ...c, smtp_use_ssl: !c.smtp_use_ssl, smtp_use_tls: false }))}
                className={`w-10 h-6 rounded-full flex items-center transition-all px-0.5 ${cfg.smtp_use_ssl ? 'bg-blue-500' : 'bg-gray-200'}`}>
                <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform ${cfg.smtp_use_ssl ? 'translate-x-4' : 'translate-x-0'}`} />
              </div>
              <span className="text-sm font-medium text-gray-700">SSL</span>
              <span className="text-xs text-gray-400">(para porta 465)</span>
            </label>
          </div>

          {/* Help box */}
          <div className="p-3 rounded-lg bg-amber-50 border border-amber-100 text-xs text-amber-800 space-y-1">
            <p className="font-semibold">⚠️ Gmail — use App Password (não a senha da conta)</p>
            <p>1. Ative verificação em 2 etapas na conta Google</p>
            <p>2. Acesse <span className="font-mono">myaccount.google.com/apppasswords</span></p>
            <p>3. Crie um App Password para "Outro (NexoSynX)" e use como senha aqui</p>
          </div>

          <div className="flex items-center justify-between pt-1">
            <Button variant="outline" onClick={testConnection} disabled={testing || !configured}
              className="text-blue-600 border-blue-300 hover:bg-blue-50">
              {testing ? '⏳ Testando…' : '🔌 Testar Conexão'}
            </Button>
            <Button onClick={save} disabled={saving} className="btn-primary px-8">
              {saving ? 'Salvando…' : '💾 Salvar SMTP'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function Integrations({ api }) {
  const [activeTab, setActiveTab] = useState('whatsapp');
  const [synklyConfig, setSynklyConfig] = useState(null);
  const [configLoading, setConfigLoading] = useState(true);

  const fetchConfig = async () => {
    try {
      const res = await api.get('/synkly/config');
      setSynklyConfig(res.data);
    } catch { setSynklyConfig({ configured: false }); }
    finally { setConfigLoading(false); }
  };

  useEffect(() => { fetchConfig(); }, []);

  const tabs = [
    { id: 'whatsapp',    label: 'WhatsApp',   icon: '📱', activeColor: 'text-green-700' },
    { id: 'linkedin',    label: 'LinkedIn',   icon: '🔵', activeColor: 'text-blue-700' },
    { id: 'groq',        label: 'Groq',       icon: '🧠', activeColor: 'text-violet-700' },
    { id: 'pexels',      label: 'Pexels',     icon: '🖼️', activeColor: 'text-sky-700' },
    { id: 'musicapi',    label: 'MusicAPI',   icon: '🎼', activeColor: 'text-amber-700' },
    { id: 'twilio',      label: 'Twilio',     icon: '📞', activeColor: 'text-red-700' },
    { id: 'elevenlabs',  label: 'ElevenLabs', icon: '🎙️', activeColor: 'text-purple-700' },
    { id: 'smtp',        label: 'SMTP',       icon: '📧', activeColor: 'text-orange-700' },
  ];

  if (configLoading) return (
    <div className="flex items-center justify-center h-48 text-gray-400">
      <span className="animate-spin mr-2">⏳</span> Carregando…
    </div>
  );

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Integrações Gerais</h1>
        <p className="text-gray-500 mt-1 text-sm">Conecte e gerencie todas as integrações externas do NexoSynX</p>
      </div>

      <SynklyConfigBanner api={api} config={synklyConfig} onSaved={fetchConfig} />

      <div className="flex flex-wrap gap-1 bg-gray-100 p-1 rounded-xl w-fit">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold transition-all ${
              activeTab === tab.id ? `bg-white shadow-sm ${tab.activeColor}` : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <span className="text-base">{tab.icon}</span>
            {tab.label}
          </button>
        ))}
      </div>

      <div>
        {activeTab === 'whatsapp'   && <WhatsAppTab   api={api} configured={synklyConfig?.configured} />}
        {activeTab === 'linkedin'   && <LinkedInTab   api={api} configured={synklyConfig?.configured} />}
        {activeTab === 'groq'       && <GroqTab       api={api} />}
        {activeTab === 'pexels'     && <PexelsTab     api={api} />}
        {activeTab === 'musicapi'   && <MusicAPITab   api={api} />}
        {activeTab === 'twilio'     && <TwilioTab     api={api} />}
        {activeTab === 'elevenlabs' && <ElevenLabsTab api={api} />}
        {activeTab === 'smtp'       && <SMTPTab       api={api} />}
      </div>
    </div>
  );
}
